import React, { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText, Shield, Trash2, Rocket } from 'lucide-react';
import { Label } from '@/components/atoms/texts/label';
import { Button } from '@/components/molecules/buttons/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import LineArt from '@/assets/images/lineArt.png';

import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarInset,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider,
    SidebarRail,
    SidebarTrigger,
} from '@/components/ui/sidebar';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import useConditions from '@/services/admin-services/query/useConditions';
import z from 'zod';

const MANAGEMENT_SIDEBAR_COOKIE_NAME = 'management_sidebar_state';
const MANAGEMENT_SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const getCookieValue = (name) => {
    if (typeof document === 'undefined') {
        return null;
    }

    const match = document.cookie
        .split('; ')
        .find((row) => row.startsWith(`${name}=`));

    return match ? match.split('=')[1] : null;
};

const formatVersionLabel = (version, t, publishedVersionNumber, detailed = false) => {
    if (!version) return '';

    if (!detailed) {
        return `v${version.versionNumber}`;
    }

    const updatedAt = new Date(version.updatedAt);
    const formattedUpdatedDate = updatedAt.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

    const isPublished = version.versionNumber === publishedVersionNumber;
    let publishedLabel = t('management.header.notPublished');

    if (isPublished && version.publishedAt) {
        const publishedDate = new Date(version.publishedAt);
        const formattedPublishedDate = publishedDate.toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
        publishedLabel = `${t('management.header.publishedAt')} ${formattedPublishedDate}`;
    }

    return `v${version.versionNumber} • ${formattedUpdatedDate} • ${publishedLabel}`;
};

const conditionLabelPathByType = {
    TERMS_OF_SERVICE: 'management.website.conditions.termsOfService',
    PRIVACY_POLICY: 'management.website.conditions.privacyPolicy',
    DATA_DELETION_INSTRUCTIONS: 'management.website.conditions.dataDeletionInstructions',
};

const conditionIconByType = {
    TERMS_OF_SERVICE: FileText,
    PRIVACY_POLICY: Shield,
    DATA_DELETION_INSTRUCTIONS: Trash2,
};

// Decommissionnement UI de la gestion des conditions du site (mentions legales) :
// conditions d'utilisation, politique de confidentialite et instructions de suppression des donnees
// ne sont plus exposees dans Kubrik. Tout le code sous-jacent (editeur HTML, gestion des versions,
// publication) est conserve et reste fonctionnel : repasser ce flag a true pour reactiver la section.
const IS_WEBSITE_CONDITIONS_ENABLED = false;

const Management = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { type: selectedConditionType } = useParams();
    const [activeVersionNumber, setActiveVersionNumber] = useState(null);
    const [activeVersionPublished, setActiveVersionPublished] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);
    const [pendingNavigationPath, setPendingNavigationPath] = useState(null);
    const [pendingVersionNumber, setPendingVersionNumber] = useState(null);
    const [availableVersions, setAvailableVersions] = useState({
        versions: [],
        selectedVersionNumber: null,
        setSelectedVersionNumber: null,
        publishedVersionNumber: null,
    });
    const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
        const cookieValue = getCookieValue(MANAGEMENT_SIDEBAR_COOKIE_NAME);

        if (cookieValue === 'false') {
            return false;
        }

        if (cookieValue === 'true') {
            return true;
        }

        return true;
    });
    const [t] = useTranslation('global');
    const { conditions, loading, error } = useConditions({ skip: !IS_WEBSITE_CONDITIONS_ENABLED });

    const isOnboardingSection = location.pathname.startsWith('/management/onboarding');
    const isWebsiteSection = IS_WEBSITE_CONDITIONS_ENABLED && !isOnboardingSection;
    const availableConditionTypes = conditions?.map((condition) => condition.type) || [];
    const activeConditionType = availableConditionTypes.includes(selectedConditionType)
        ? selectedConditionType
        : availableConditionTypes[0];

    useEffect(() => {
        if (isWebsiteSection && activeConditionType && selectedConditionType !== activeConditionType) {
            navigate(`/management/website/${activeConditionType}`, { replace: true });
        }
    }, [activeConditionType, isWebsiteSection, navigate, selectedConditionType]);

    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        document.cookie = `${MANAGEMENT_SIDEBAR_COOKIE_NAME}=${isSidebarOpen}; path=/; max-age=${MANAGEMENT_SIDEBAR_COOKIE_MAX_AGE}`;
    }, [isSidebarOpen]);

    const getConditionLabel = (type) => {
        const translationKey = conditionLabelPathByType[type];

        return translationKey ? t(translationKey) : type;
    };

    const sortedConditions = [...(conditions || [])].sort((a, b) => getConditionLabel(a.type).localeCompare(
        getConditionLabel(b.type),
        undefined,
        { sensitivity: 'base' },
    ));

    const getConditionIcon = (type) => conditionIconByType[type] || FileText;

    const requestLeave = ({ path = null, versionNumber = null }) => {
        if (!hasUnsavedChanges) {
            if (path) {
                navigate(path);
                return;
            }

            if (versionNumber !== null && typeof availableVersions.setSelectedVersionNumber === 'function') {
                availableVersions.setSelectedVersionNumber(versionNumber);
            }

            return;
        }

        setPendingNavigationPath(path);
        setPendingVersionNumber(versionNumber);
        setIsLeaveConfirmOpen(true);
    };

    const handleConfirmLeave = () => {
        if (pendingNavigationPath) {
            navigate(pendingNavigationPath);
        }

        if (pendingVersionNumber !== null && typeof availableVersions.setSelectedVersionNumber === 'function') {
            availableVersions.setSelectedVersionNumber(pendingVersionNumber);
        }

        setPendingNavigationPath(null);
        setPendingVersionNumber(null);
        setIsLeaveConfirmOpen(false);
    };

    const handleCancelLeave = () => {
        setPendingNavigationPath(null);
        setPendingVersionNumber(null);
        setIsLeaveConfirmOpen(false);
    };

    const handleConditionClick = (type) => {
        requestLeave({ path: `/management/website/${type}` });
    };

    const handleOnboardingClick = () => {
        requestLeave({ path: '/management/onboarding' });
    };

    const headerTitle = isWebsiteSection && activeConditionType
        ? `${t('management.header.modificationOf')} ${getConditionLabel(activeConditionType)}`
        : t('management.onboarding.title');

    const versionCaption = activeVersionNumber !== null && activeVersionNumber !== undefined
        ? `v ${activeVersionNumber}`
        : null;

    const publicationCaption = activeVersionPublished === null
        ? null
        : activeVersionPublished
            ? t('management.header.published')
            : t('management.header.notPublished');

    return (
        <SidebarProvider
            open={isSidebarOpen}
            onOpenChange={setIsSidebarOpen}
            style={{ '--sidebar-width': '15rem' }}
        >
            <Sidebar variant="inset" collapsible="icon" className="md:!top-[70px] md:!h-[calc(100svh-70px)]">
                <SidebarHeader className="group-data-[collapsible=icon]:hidden">
                    <Label message="management.title" size="h4" fontWeight="bold" className="px-2 py-1" />
                </SidebarHeader>
                <SidebarContent>
                     <SidebarGroup>
                        <SidebarGroupLabel className="group-data-[collapsible=icon]:hidden">
                            <Label color="primaryStrong" className="uppercase" fontWeight="medium">
                                {t('management.groups.onboarding')}
                            </Label>
                        </SidebarGroupLabel>
                        <SidebarGroupContent>
                            <SidebarMenu>
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        isActive={isOnboardingSection}
                                        onClick={handleOnboardingClick}
                                        tooltip={t('management.onboarding.title')}
                                    >
                                        <Rocket />
                                        <Label>{t('management.onboarding.title')}</Label>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                    {/* Section "Site internet" (conditions / mentions legales) decommissionnee cote UI.
                        Conservee et pilotee par IS_WEBSITE_CONDITIONS_ENABLED pour pouvoir etre reactivee. */}
                    {IS_WEBSITE_CONDITIONS_ENABLED && (
                    <SidebarGroup>
                        <SidebarGroupLabel className="group-data-[collapsible=icon]:hidden">
                            <Label color="primaryStrong" className="uppercase" fontWeight="medium">
                                {t('management.groups.website')}
                            </Label>
                        </SidebarGroupLabel>
                        <SidebarGroupContent>
                            <SidebarMenu >
                                {/* <SidebarMenu className="border-l border-grey ml-3 pl-2 group-data-[collapsible=icon]:border-l-0 group-data-[collapsible=icon]:ml-0 group-data-[collapsible=icon]:pl-0"> */}
                                {loading && (
                                    <SidebarMenuItem>
                                        <SidebarMenuButton disabled>
                                            <span className='loader'/>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                )}
                                {!loading && error && (
                                    <SidebarMenuItem>
                                        <SidebarMenuButton disabled>
                                            <span>{t('error.fetchUnsuccessfull')}</span>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                )}
                                {!loading && !error && sortedConditions.map((condition) => (
                                    <SidebarMenuItem key={condition.uuid}>
                                        {(() => {
                                            const ConditionIcon = getConditionIcon(condition.type);

                                            return (
                                                <SidebarMenuButton
                                                    isActive={isWebsiteSection && activeConditionType === condition.type}
                                                    onClick={() => handleConditionClick(condition.type)}
                                                    tooltip={getConditionLabel(condition.type)}
                                                >
                                                    <ConditionIcon />
                                                    <Label>{getConditionLabel(condition.type)}</Label>
                                                </SidebarMenuButton>
                                            );
                                        })()}
                                    </SidebarMenuItem>
                                ))}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                    )}
                   
                </SidebarContent>
                <SidebarRail />
            </Sidebar>

            <SidebarInset className="h-[calc(100svh-70px)] min-h-[calc(100svh-70px)] flex flex-col ">
                <div className="flex items-center gap-secondDegree border-b px-secondDegree py-secondDegree ">
                    
                    <SidebarTrigger />
                    <div className="flex items-center gap-firstDegree flex-1">
                        <Label size="h3" fontWeight="bold">{headerTitle}</Label>
                       {isWebsiteSection && versionCaption && availableVersions.versions.length > 0 && (
                            <Select
                                value={String(availableVersions.selectedVersionNumber)}
                                onValueChange={(value) => {
                                    if (typeof availableVersions.setSelectedVersionNumber === 'function') {
                                        requestLeave({ versionNumber: Number(value) });
                                    }
                                }}
                            >
                                <SelectTrigger className="w-auto min-w-fit border-0 hover:bg-hover">
                                    <SelectValue>
                                        {`v${availableVersions.selectedVersionNumber}`}
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    {availableVersions.versions.map((version) => (
                                        <SelectItem key={version.uuid} value={String(version.versionNumber)}>
                                            <Label>{formatVersionLabel(version, t, availableVersions.publishedVersionNumber, true)}</Label>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        {isWebsiteSection && hasUnsavedChanges && (
                            <div  className={`rounded-sm  px-firstDegree py-1 border ` + (activeVersionPublished ? ' bg-information20 border-information' : ' bg-warning20 border-warning') }>
                                <Label size="small">{t('management.header.unsavedChanges')}</Label>
                            </div>
                        )}
                        {isWebsiteSection && publicationCaption && (
                            <div  className={`rounded-sm  px-firstDegree py-1 border ` + (activeVersionPublished ? ' bg-information20 border-information' : ' bg-warning20 border-warning') }>
                                <Label size="small" >{publicationCaption}</Label>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex-1 min-h-0 px-secondDegree py-thirdDegree z-10 w-full overflow-x-hidden relative">
                                                        <img src={LineArt} onDragStart={(event) => event.preventDefault()} className=" bgLineArt " />

                    <Outlet context={{
                        selectedConditionType: activeConditionType,
                        selectedConditionLabel: activeConditionType ? getConditionLabel(activeConditionType) : undefined,
                        setActiveVersionNumber,
                        setActiveVersionPublished,
                        setHasUnsavedChanges,
                        setAvailableVersions,
                    }} />
                </div>
            </SidebarInset>

            <Dialog open={isLeaveConfirmOpen} onOpenChange={setIsLeaveConfirmOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            <Label message="management.website.editor.unsavedDialog.title" />
                        </DialogTitle>
                        <DialogDescription>
                            <Label message="management.website.editor.unsavedDialog.description" />
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button style="ghost" text="global.actions.cancel" showText onClick={handleCancelLeave} />
                        <Button style="fill" text="management.website.editor.unsavedDialog.confirmLeave" showText onClick={handleConfirmLeave} />
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </SidebarProvider>
    );
};

export default Management;
